import React, { useState } from 'react';

import { Check, Eye, EyeOff } from 'lucide-react';

import MemberCourseCell from './MemberCourseCell';
import { useHomeClubActions } from '../../hooks/useHomeClubActions';
import { MemberHomeCourseSummaryDTO } from '../../services/apiClient';
import AddNewButton from '../common/AddButton';
import Table from '../common/Table';
import CoursePickerDrawer from '../drawers/CoursePickerDrawer';

interface HomeCoursesTableProps {
  // Narrows the rows by course name. Lower-cased and trimmed by the caller.
  search?: string;
  // Pads the hint and button in from the edges, for a home with no padding of its own (the golf
  // lists). A Card already pads its content.
  inset?: boolean;
}

// The member's home courses: add, remove, make one primary (the one their profile shows), and show
// or hide each one on their profile. One
// component, two homes: the desktop Home Courses page, and the Home tab of the profile's golf
// lists (/member/profile/golf), the phone's way in. The actions live in useHomeClubActions.
const HomeCoursesTable: React.FC<HomeCoursesTableProps> = ({
  search = '',
  inset = false,
}) => {
  const {
    homeCourses,
    isLoading,
    homeCourseIds,
    add,
    remove,
    setHidden,
    setPrimary,
    savingCourseId,
  } = useHomeClubActions();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const rows =
    search.length > 0
      ? homeCourses.filter((row) =>
          (row.courseName ?? '').toLowerCase().includes(search),
        )
      : homeCourses;

  return (
    <>
      <div
        className={`flex flex-wrap items-center justify-between gap-3 pb-3 ${
          inset ? 'px-4 pt-3' : ''
        }`}
      >
        <p className="min-w-0 flex-1 basis-56 text-sm text-ink-muted dark:text-white/60">
          Courses you&apos;ve marked as home. Your primary one is the one your
          profile shows: tap Make primary to change it.
        </p>
        <AddNewButton label="Add" onClick={() => setIsPickerOpen(true)} />
      </div>

      <Table
        columns={[
          {
            label: 'Course',
            key: 'courseName',
            className: '!px-2 sm:!px-4',
            render: (row: MemberHomeCourseSummaryDTO) => (
              <MemberCourseCell
                courseId={row.courseId}
                name={row.courseName}
                subtitle={row.courseAddress}
                logoFileId={row.courseLogoFileId}
                closed={row.courseClosed}
              />
            ),
          },
          {
            // The one home course the profile shows. Exactly one row is primary (the backend
            // falls back to the first by name), so the column always marks one. No star on
            // purpose (the user's call, 2026-10-19): a filled gold pill marks the primary, an
            // outlined one on every other row makes that row primary when tapped.
            label: 'Primary',
            key: 'isPrimary',
            className: '!px-2 sm:!px-4',
            render: (row: MemberHomeCourseSummaryDTO): React.ReactElement =>
              row.isPrimary ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-3 py-1 text-xs font-semibold text-secondary">
                  <Check className="h-4 w-4" /> Primary
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setPrimary(row)}
                  disabled={savingCourseId === row.courseId}
                  aria-label={`Make ${row.courseName ?? 'this course'} my primary home course`}
                  className="inline-flex items-center whitespace-nowrap rounded-full border border-primary px-3 py-1 text-xs font-semibold text-primary-deep transition-colors hover:bg-primary hover:text-secondary disabled:cursor-wait disabled:opacity-60 dark:text-primary dark:hover:text-secondary"
                >
                  Make primary
                </button>
              ),
          },
          {
            // The legacy Private flag. A hidden home course still counts everywhere (the Explore
            // filter, the map) but is left off the member's profile.
            label: 'On my profile',
            key: 'private',
            className: '!px-2 sm:!px-4',
            render: (row: MemberHomeCourseSummaryDTO): React.ReactElement => {
              const hidden = row.private === true;
              return (
                <button
                  type="button"
                  onClick={() => setHidden(row, !hidden)}
                  disabled={savingCourseId === row.courseId}
                  aria-label={
                    hidden
                      ? `Show ${row.courseName ?? 'course'} on my profile`
                      : `Hide ${row.courseName ?? 'course'} from my profile`
                  }
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-ink transition-colors hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                >
                  {hidden ? (
                    <>
                      <EyeOff className="h-4 w-4" /> Hidden
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" /> Visible
                    </>
                  )}
                </button>
              );
            },
          },
        ]}
        data={rows}
        isLoading={isLoading}
        allowPaging={false}
        expandableDetails={false}
        actions={{
          onDelete: (row: MemberHomeCourseSummaryDTO) => void remove(row),
        }}
        noDataMessage={
          search.length > 0
            ? 'No course matches that.'
            : "You haven't set a home course yet."
        }
      />

      <CoursePickerDrawer
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        title="Add a home course"
        selectedCourseIds={homeCourseIds}
        onAdd={add}
      />
    </>
  );
};

export default HomeCoursesTable;
